/**
 * Definitions PARTAGEES entre l'ecran Contacts et son ecran de reglages
 * (settings.tsx) : catalogue des filtres de la porte, catalogue des stats
 * d'en-tete, cles de prefs et valeurs par defaut. Un seul endroit, zero
 * duplication.
 *
 * Les prefs sont locales au poste (localStorage via readPref/usePref de
 * @taki/ui) et suivent les cles standard du socle :
 *  - "app.contact.filters" : Record<filterId, boolean> (visibilite des filtres) ;
 *  - "app.contact.stats"   : string[] (ids ordonnes des stats affichees, max 3).
 */

/** Ids des filtres declares par la porte (popover de l'outil filtre, zone 3b). */
export type ContactFilterId = 'with-email' | 'without-email';

/**
 * LE CATALOGUE DE FILTRES de la porte (gabarit v2) : multi-choix additif, un
 * filtre actif = un tag retirable dans le tiroir. Il n'y a PAS d'entree "Tous" :
 * l'etat de repos (aucun filtre) montre tout, et l'entree de sortie du popover
 * ("Tout afficher") appartient au chassis, elle vide la selection. La
 * VISIBILITE de chaque entree est editable dans settings.tsx ; les valeurs
 * restent figees (v0).
 */
export const FILTER_DEFS: ReadonlyArray<{ id: ContactFilterId; label: string }> = [
  { id: 'with-email', label: 'Avec email' },
  { id: 'without-email', label: 'Sans email' },
];

/**
 * Le libelle du REPLI du catalogue, tel que le popover le rend (entree de
 * sortie du chassis). Les reglages l'affichent en premiere ligne, verrouillee
 * "toujours visible" : ce repli ne se masque pas, c'est lui qui garantit
 * qu'on peut toujours revenir a "tout".
 */
export const FILTER_FALLBACK_LABEL = 'Tout afficher';

/** Cle de pref : visibilite des filtres du catalogue. Cle absente = visible. */
export const FILTERS_PREF_KEY = 'app.contact.filters';

/** Defaut filtres : tout le catalogue visible. */
export const DEFAULT_FILTERS: Record<string, boolean> = Object.fromEntries(
  FILTER_DEFS.map((def) => [def.id, true]),
);

/** Ids des statistiques disponibles pour l'en-tete (zone 2c). */
export type ContactStatId = 'total' | 'with-email' | 'without-email' | 'with-phone';

/**
 * Catalogue des stats affichables en haut a droite. La SELECTION (max 3) est
 * editable dans settings.tsx ; l'ordre d'affichage suit ce catalogue et les
 * valeurs restent calculees des donnees reelles par l'ecran.
 */
export const STAT_DEFS: ReadonlyArray<{ id: ContactStatId; label: string }> = [
  { id: 'total', label: 'contacts' },
  { id: 'with-email', label: 'avec email' },
  { id: 'without-email', label: 'sans email' },
  { id: 'with-phone', label: 'avec telephone' },
];

/** Nombre maximal de stats affichees (contrat de la cle "app.<id>.stats"). */
export const STATS_MAX = 3;

/** Cle de pref : ids ordonnes des stats affichees. */
export const STATS_PREF_KEY = 'app.contact.stats';

/** Defaut stats : les 3 premieres du catalogue. */
export const DEFAULT_STATS: string[] = STAT_DEFS.slice(0, STATS_MAX).map((def) => def.id);
